import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import Icon from '@icon/Icon';
import { FloatingActionSectionType, FloatingActionButtonVisibility, getAllSections, onRegistryChange } from './floatingActionsRegistry';
import { WidgetDrawer } from '@core/dashboard/widget-drawer/WidgetDrawer';
/**
 * @export
 * FloatingActions Component
 *
 * Consolidated floating action buttons that handles both page and dashboard sections.
 * Renders sections horizontally when multiple sections are registered.
 *
 * @example
 * ```typescript
 * // Placed in ConfiguredPage - automatically renders based on registrations
 * <FloatingActions />
 * ```
 */
const FloatingActions = () => {
    const [sections, setSections] = useState([]);
    const [showWidgetDrawer, setShowWidgetDrawer] = useState(false);
    const [activeDrawerSectionId, setActiveDrawerSectionId] = useState(null);
    const nodeRef = React.useRef(null);
    // Subscribe to registry changes and provide controls to dashboard sections
    useEffect(() => {
        const allSections = getAllSections();
        setSections(allSections);
        // Provide control methods to dashboard sections
        allSections.forEach(section => {
            if (section.type === FloatingActionSectionType.Dashboard && section.onRegister) {
                section.onRegister({
                    openWidgetDrawer: () => {
                        setActiveDrawerSectionId(section.id);
                        setShowWidgetDrawer(true);
                    },
                    closeWidgetDrawer: () => {
                        if (activeDrawerSectionId === section.id) {
                            setShowWidgetDrawer(false);
                        }
                    }
                });
            }
        });
        const unsubscribe = onRegistryChange(() => {
            const updatedSections = getAllSections();
            setSections(updatedSections);
            // Re-provide controls when registry changes
            updatedSections.forEach(section => {
                if (section.type === FloatingActionSectionType.Dashboard && section.onRegister) {
                    section.onRegister({
                        openWidgetDrawer: () => {
                            setActiveDrawerSectionId(section.id);
                            setShowWidgetDrawer(true);
                        },
                        closeWidgetDrawer: () => {
                            if (activeDrawerSectionId === section.id) {
                                setShowWidgetDrawer(false);
                            }
                        }
                    });
                }
            });
        });
        return unsubscribe;
    }, [activeDrawerSectionId]);
    // No sections registered - don't render anything
    if (sections.length === 0) {
        return null;
    }
    // Helper function to determine if a button should be shown based on visibility and edit mode
    const shouldShowButton = (visibility = FloatingActionButtonVisibility.Always, isEditing) => {
        if (visibility === FloatingActionButtonVisibility.Always)
            return true;
        if (visibility === FloatingActionButtonVisibility.Normal)
            return !isEditing;
        if (visibility === FloatingActionButtonVisibility.Edit)
            return isEditing;
        return true;
    };
    // Open widget drawer for specific section
    const openWidgetDrawer = (sectionId) => {
        setActiveDrawerSectionId(sectionId);
        setShowWidgetDrawer(true);
    };
    // Render dashboard section buttons
    const renderDashboardSection = (section) => {
        var _a;
        return (_jsxs(_Fragment, { children: [section.enableAdd && shouldShowButton(section.addButtonVisibility || FloatingActionButtonVisibility.Always, section.isEditing) && (_jsx("div", { className: "uxpcore_floatingactions__button", title: 'Add widget', children: _jsx(ButtonComponent, { leftIcon: _jsx(Icon, { icon: "phl plus" }), iconOnly: true, variant: "secondary", onClick: () => openWidgetDrawer(section.id), className: "uxpcore_floatingactions__button--rounded", title: "Add widget" }) })), (_a = section.customActions) === null || _a === void 0 ? void 0 : _a.map((action, index) => shouldShowButton(action.visibility || FloatingActionButtonVisibility.Always, section.isEditing) && (_jsx("div", { className: "uxpcore_floatingactions__button", title: action.tooltip || '', children: _jsx(ButtonComponent, { leftIcon: _jsx(Icon, { icon: action.icon }), iconOnly: true, variant: action.variant || 'secondary', onClick: action.onClick, className: "uxpcore_floatingactions__button--rounded", title: action.tooltip }) }, index))), !section.isEditing && section.enableEdit && (_jsx("div", { className: "uxpcore_floatingactions__button", title: 'Edit dashboard', children: _jsx(ButtonComponent, { leftIcon: ["phl", "squares-four"], iconOnly: true, variant: "secondary", onClick: section.onEnterEditMode, className: "uxpcore_floatingactions__button--rounded", title: "Edit dashboard" }) })), section.isEditing && (_jsx("div", { className: "uxpcore_floatingactions__button", title: 'Done editing', children: _jsx(ButtonComponent, { leftIcon: _jsx(Icon, { icon: "phl check" }), iconOnly: true, variant: "primary", onClick: section.onExitEditMode, className: "uxpcore_floatingactions__button--rounded", title: "Done editing" }) }))] }));
    };
    // Render page section buttons
    const renderPageSection = (section) => {
        var _a;
        return (_jsxs(_Fragment, { children: [(_a = section.customActions) === null || _a === void 0 ? void 0 : _a.map((action, index) => shouldShowButton(action.visibility || FloatingActionButtonVisibility.Always, section.isEditing) && (_jsx("div", { className: "uxpcore_floatingactions__button", title: action.tooltip || '', children: _jsx(ButtonComponent, { leftIcon: _jsx(Icon, { icon: action.icon }), iconOnly: true, variant: action.variant || 'secondary', onClick: action.onClick, className: "uxpcore_floatingactions__button--rounded", title: action.tooltip }) }, index))), !section.isEditing && (_jsx("div", { className: "uxpcore_floatingactions__button", title: 'Edit Page', children: _jsx(ButtonComponent, { leftIcon: _jsx(Icon, { icon: "phl pencil" }), iconOnly: true, variant: "secondary", onClick: section.onEnterEditMode, className: "uxpcore_floatingactions__button--rounded", title: "Edit page" }) })), section.isEditing && (_jsx("div", { className: "uxpcore_floatingactions__button", title: 'Done editing', children: _jsx(ButtonComponent, { leftIcon: _jsx(Icon, { icon: "phl check" }), iconOnly: true, variant: "primary", onClick: section.onExitEditMode, className: "uxpcore_floatingactions__button--rounded", title: "Done editing" }) }))] }));
    };
    // Find active drawer section
    const activeDrawerSection = sections.find(s => s.id === activeDrawerSectionId);
    const isDashboardSection = (activeDrawerSection === null || activeDrawerSection === void 0 ? void 0 : activeDrawerSection.type) === FloatingActionSectionType.Dashboard;
    // Determine which sections to render: only editing section if any is editing, otherwise all
    const editingSection = sections.find(s => s.isEditing);
    const sectionsToRender = editingSection ? [editingSection] : sections;
    sectionsToRender.sort((a, b) => a.id.localeCompare(b.id));
    // Hide the entire panel (including drag handle) when no section has any visible buttons.
    // Custom actions with Edit-only visibility don't count unless we're already editing.
    const hasAnyButtons = sectionsToRender.some(s => {
        var _a, _b;
        if (s.type === FloatingActionSectionType.Dashboard) {
            const ds = s;
            if (ds.isEditing)
                return true;
            if (ds.enableEdit || ds.enableAdd)
                return true;
            const hasVisibleCustomActions = (_b = (_a = ds.customActions) === null || _a === void 0 ? void 0 : _a.some(a => a.visibility !== FloatingActionButtonVisibility.Edit)) !== null && _b !== void 0 ? _b : false;
            return hasVisibleCustomActions;
        }
        // Page section: always has edit button
        return true;
    });
    if (!hasAnyButtons)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(Draggable, { nodeRef: nodeRef, bounds: "body", defaultPosition: { x: 0, y: 0 }, handle: ".uxpcore_floatingactions__draghandle", children: _jsx("div", { ref: nodeRef, className: "uxpcore_floatingactions", children: _jsxs("div", { className: "uxpcore_floatingactions__container", children: [_jsx(ButtonComponent, { leftIcon: "fal arrows", iconOnly: true, variant: "secondary", className: "uxpcore_floatingactions__draghandle uxpcore_floatingactions__button--rounded", title: "Drag" }), sectionsToRender.map(section => (_jsx("div", { className: "uxpcore_floatingactions__section", children: section.type === FloatingActionSectionType.Dashboard
                                    ? renderDashboardSection(section)
                                    : renderPageSection(section) }, section.id)))] }) }) }), isDashboardSection && activeDrawerSection && (_jsx(WidgetDrawer, { show: showWidgetDrawer, widgets: activeDrawerSection.widgets, layouts: activeDrawerSection.layouts, onClose: () => setShowWidgetDrawer(false), onChange: activeDrawerSection.onChangeWidgetDrawer, isBounded: activeDrawerSection.isBounded, maxColumns: activeDrawerSection.maxColumns, config: activeDrawerSection.widgetDrawerConfig }))] }));
};
export default FloatingActions;
